import type { CellResult, GuessResult } from '../lib/types';
import { displayTitle } from '../lib/game';
import { useApp } from '../app-context';

const COLS: Array<[string, string]> = [
  ['col.anime', 'Anime'], ['col.year', 'Year'], ['col.season', 'Season'],
  ['col.eps', 'Eps'], ['col.studio', 'Studio'], ['col.genres', 'Genres'], ['col.score', 'Score'],
];

function cls(c: CellResult): string {
  return c.state === 'correct' ? 'g' : c.state === 'partial' ? 'y' : 'r';
}

function arrow(c: CellResult) {
  if (!c.direction) return null;
  return <span className="arw">{c.direction === 'up' ? '▲' : '▼'}</span>;
}

// Flips only the most recent row, column-staggered (best-practice §11.7).
export default function ResultsGrid({ results }: { results: GuessResult[] }) {
  const { t } = useApp();
  const season = (s: string | null) => (s ? t(`season.${s}`) : '—');
  if (!results.length) return null;
  const rows = [...results].reverse(); // newest on top
  return (
    <div className="grid">
      <table>
        <thead>
          <tr>{COLS.map(([k, fallback]) => <th key={k}>{t(k) === k ? fallback : t(k)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const flip = idx === 0; // top row is the newest guess
            const fc = flip ? 'flip' : '';
            const style = (i: number) => (flip ? { animationDelay: `${i * 0.07}s` } : undefined);
            return (
              <tr key={r.anime.id}>
                <td className={`cover ${fc}`} style={style(0)}>
                  {r.anime.cover && <img src={r.anime.cover} alt={displayTitle(r.anime)} />}
                </td>
                <td className={`${cls(r.year)} ${fc}`} style={style(1)}>{r.anime.year ?? '—'}{arrow(r.year)}</td>
                <td className={`${cls(r.season)} ${fc}`} style={style(2)}>{season(r.anime.season)}</td>
                <td className={`${cls(r.episodes)} ${fc}`} style={style(3)}>{r.anime.episodes ?? '—'}{arrow(r.episodes)}</td>
                <td className={`${cls(r.studio)} ${fc}`} style={style(4)}>{r.anime.studio ?? '—'}</td>
                <td className={`${cls(r.genres)} ${fc}`} style={style(5)}>{r.anime.genres.slice(0, 2).join(' · ')}</td>
                <td className={`${cls(r.score)} ${fc}`} style={style(6)}>{r.anime.score ? (r.anime.score / 10).toFixed(1) : '—'}{arrow(r.score)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="legend">
        <span><i className="chip" style={{ background: 'var(--green)' }} />{t('legend.match')}</span>
        <span><i className="chip" style={{ background: 'var(--yellow)' }} />{t('legend.close')}</span>
        <span><i className="chip" style={{ background: 'var(--grayred)' }} />{t('legend.no')}</span>
        <span>{t('legend.dir')}</span>
      </div>
    </div>
  );
}
